import axios from "axios"
import {
    BANK_ACTIVITIES_ENDPOINT,
    BANK_ACTIVITIES_ENDPOINT_UPDATE,
    CANCEL_BANK_ACTIVITIES_ENDPOINT,
    APPLY_BANK_ACTIVITIES,
    UPLOAD_LAYOUT_ENDPOINT
} from "../../../../../../../utility/SourceAPI"
import { getDateToFilterAPI } from "../../../../../../../utility/formats/formattedDate"

/**
 * Única capa que habla con el backend de actividades bancarias (transferencias).
 * Devuelve siempre `response.data`, nunca el envoltorio de axios, y deja que el
 * error suba tal cual: quien decide qué hacer con él es el thunk (rejectWithValue).
 */

const multipartConfig = {
    headers: {
        'Content-Type': 'multipart/form-data;boundary=boundary'
    }
}

//Las actividades tomadas se filtran por fecha de asignación y las aplicadas por fecha de aplicación
const MY_ACTIVITIES_DATE_FIELD = {
    taken: 'assigned_at',
    applied: 'done_on'
}

export const fetchAllBankActivities = async (currency, rangeDates) => {
    const response = await axios.get(rangeDates !== undefined ? BANK_ACTIVITIES_ENDPOINT.concat(`?${getDateToFilterAPI(rangeDates)}&currency=${currency}`) : BANK_ACTIVITIES_ENDPOINT.concat(`?currency=${currency}`))
    return response.data
}

//El rango cierra a las 23:59:59 del último día, como en las demás lecturas. Antes la hora de cierre era la de carga
//del módulo (23:mm:ss), así que dejaba fuera lo del final del día y a los 59 minutos armaba 23:60
export const fetchBankActivitiesIntermediary = async ({ id, rangeDates }) => {
    const response = await axios.get(rangeDates !== undefined ? BANK_ACTIVITIES_ENDPOINT.concat(`?${getDateToFilterAPI(rangeDates)}&intermediary_id=${id}`) : BANK_ACTIVITIES_ENDPOINT.concat(`?intermediary_id=${id}`))
    return response.data
}

export const fetchBankActivitiesToTransfer = async () => {
    const response = await axios.get(BANK_ACTIVITIES_ENDPOINT.concat('is_transferable'))
    return response.data
}

export const fetchBankActivitiesAssigned = async ({ id, currency }) => {
    const response = await axios.get(BANK_ACTIVITIES_ENDPOINT.concat(`?taken_by_id=${id}&currency=${currency || 'MXN'}`))
    return response.data
}

export const fetchBankActivitiesPending = async (currency, rangeDates) => {
    const query = `?status_str=pending&currency=${currency}`
    const response = await axios.get(BANK_ACTIVITIES_ENDPOINT.concat(rangeDates !== undefined ? `${query}&${getDateToFilterAPI(rangeDates)}` : query))
    return response.data
}

//Actividades del usuario en sesión; `department` acota a un solo departamento (Bancos-Toluca)
export const fetchMyBankActivities = async ({ status, currency, department, rangeDates }) => {
    const departmentFilter = department ? `&department=${department}` : ''
    const query = `?taken_by_me=true&status_str=${status}&filter_date_by=${MY_ACTIVITIES_DATE_FIELD[status]}&currency=${currency}${departmentFilter}`
    const response = await axios.get(BANK_ACTIVITIES_ENDPOINT.concat(rangeDates !== undefined ? `${query}&${getDateToFilterAPI(rangeDates)}` : query))
    return response.data
}

//Con rango la URL quedaba 'taken&currency=...' (sin '?'); ahora el rango se agrega al query de la moneda
export const fetchActivitiesTaken = async (currency, rangeDates) => {
    const query = `taken?currency=${currency}`
    const response = await axios.get(BANK_ACTIVITIES_ENDPOINT.concat(rangeDates !== undefined ? `${query}&${getDateToFilterAPI(rangeDates)}` : query))
    return response.data
}

export const fetchActivitiesLayout = async () => {
    const response = await axios.get(BANK_ACTIVITIES_ENDPOINT.concat('downloand_layout/'), { responseType: 'blob' })
    return response.data
}

export const uploadLayoutSipare = async (layout) => {
    const response = await axios.post(BANK_ACTIVITIES_ENDPOINT.concat('upload_csv/'), layout, multipartConfig)
    return response.data
}

export const createMultipleActivities = async (data) => {
    const response = await axios.post(BANK_ACTIVITIES_ENDPOINT.concat('upload_voucher_bank_activity'), data)
    return response.data
}

export const createBankActivity = async (bankActivity) => {
    const response = await axios.post(BANK_ACTIVITIES_ENDPOINT, bankActivity)
    return response.data
}

export const updateBankActivity = async (activityId, bankActivity) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT_UPDATE.concat(activityId), bankActivity)
    return response.data
}

export const assignActivity = async (data) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`assign`), data)
    return response.data
}

export const transferActivity = async (data) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`update_deparment/?department=${data.departement}`), data)
    return response.data
}

export const dropActivities = async (data) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`drop`), data)
    return response.data
}

export const requestTransferBankActivity = async (bankActivity) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`is_transferable/${bankActivity?.id}?is_transferable=${bankActivity?.request}`), bankActivity)
    return response.data
}

export const transferBankActivityIntermediary = async (bankActivity) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`transfer_of_intermediary/${bankActivity?.id}?intermediary_id=${bankActivity?.intermediary_id}`), bankActivity)
    return response.data
}

export const authorizeTransferBankActivity = async (bankActivity) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`authorize_transfer/${bankActivity?.id}?authorized_transfer=${bankActivity?.authorized_transfer}`), bankActivity)
    return response.data
}

export const fetchLayoutTransfers = async () => {
    const response = await axios.get(UPLOAD_LAYOUT_ENDPOINT.concat('transfers/'), { responseType: 'blob' })
    return response.data
}

export const uploadLayoutTransfers = async (layout) => {
    const response = await axios.post(UPLOAD_LAYOUT_ENDPOINT.concat('transfers/'), layout, multipartConfig)
    return response.data
}

export const cancelBankActivity = async (bankActivityId) => {
    const response = await axios.put(CANCEL_BANK_ACTIVITIES_ENDPOINT.concat(bankActivityId))
    return response.data
}

export const applyBankActivity = async (activityId, bankActivity) => {
    const response = await axios.put(APPLY_BANK_ACTIVITIES.concat(activityId), bankActivity, multipartConfig)
    return response.data
}

export const editAppliedActivity = async (activityId, bankActivity) => {
    const response = await axios.put(BANK_ACTIVITIES_ENDPOINT.concat(`edit/${activityId}`), bankActivity, multipartConfig)
    return response.data
}
